import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Upload, Download, Trash2, FileText, X, CalendarIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from '@/hooks/use-toast';
import { InitiativeFilters } from '@/components/initiatives/InitiativeFilters';
import { InitiativeList } from '@/components/initiatives/InitiativeList';

interface Initiative {
  id: string;
  name: string;
  description: string | null;
  deployment_date: string | null;
  created_at: string;
  updated_at: string;
  files?: InitiativeFile[];
  stats?: {
    clientCount: number;
    clients: Array<{ id: string; name: string }>;
    responseStatuses: {
      no_response: number;
      not_interested: number;
      interested: number;
      proposal: number;
    };
    clientsByStatus: {
      no_response: Array<{ id: string; name: string }>;
      not_interested: Array<{ id: string; name: string }>;
      interested: Array<{ id: string; name: string }>;
      proposal: Array<{ id: string; name: string }>;
    };
    activityTypes: {
      email: number;
      call: number;
      meeting: number;
      linkedin: number;
      other: number;
    };
  };
}

interface InitiativeFile {
  id: string;
  name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export default function Initiatives() {
  const [open, setOpen] = useState(false);
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative | null>(null);
  const [selectedInitiativeForFiles, setSelectedInitiativeForFiles] = useState<Initiative | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    deployment_date: ''
  });
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSort, setSelectedSort] = useState('newest');
  const [selectedDate, setSelectedDate] = useState('all');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Helper function to reset form
  const resetForm = () => {
    setFormData({ name: '', description: '', deployment_date: '' });
    setSelectedFiles([]);
    setSelectedInitiative(null);
  };

  // Helper function to handle modal close
  const handleModalClose = (open: boolean) => {
    setOpen(open);
    if (!open) {
      resetForm();
    }
  };

  // Fetch initiatives
  const { data: initiatives = [], isLoading } = useQuery({
    queryKey: ['initiatives'],
    queryFn: async () => {
      console.log('Starting initiatives query...');
      
      // First get initiatives without the join
      const { data: initiativesData, error } = await supabase
        .from('initiatives')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Initiatives query error:', error);
        throw error;
      }

      console.log('Fetched initiatives:', initiativesData?.length);

      // Get all activities for statistics with client information
      // Use a more robust query to ensure we get the data we need
      const { data: allActivities, error: activitiesError } = await supabase
        .from('activities')
        .select(`
          initiative_id, 
          client_id, 
          response_status, 
          activity_date, 
          type
        `)
        .not('initiative_id', 'is', null)
        .not('client_id', 'is', null);

      // Get all clients separately to avoid join issues
      const { data: allClients } = await supabase
        .from('clients')
        .select('id, name');

      if (activitiesError) {
        console.error('Activities query error:', activitiesError);
        // Don't throw error - just proceed with empty activities
        console.warn('Proceeding without activities data');
      }

      console.log('Fetched activities:', allActivities?.length || 0);

      // Then get files and calculate stats for each initiative
      const initiativesWithFilesAndStats = await Promise.all(
        (initiativesData || []).map(async (initiative) => {
          console.log(`Processing initiative: ${initiative.name}`);
          
          // Get files for this initiative
          const { data: files } = await supabase
            .from('initiative_files')
            .select('*')
            .eq('initiative_id', initiative.id);

          // Calculate statistics for this initiative
          const initiativeActivities = allActivities?.filter(activity => 
            activity.initiative_id === initiative.id
          ) || [];

          console.log(`Activities for ${initiative.name}:`, initiativeActivities.length);

          // Get unique clients with names
          const clientMap = new Map<string, { id: string; name: string }>();
          initiativeActivities.forEach(activity => {
            if (activity.client_id) {
              const client = allClients?.find(c => c.id === activity.client_id);
              if (client) {
                clientMap.set(activity.client_id, {
                  id: activity.client_id,
                  name: client.name
                });
              }
            }
          });
          const clients = Array.from(clientMap.values());
          const clientCount = clients.length;

          // Count response statuses by unique clients (not individual activities)
          const clientResponseMap = new Map<string, string>();
          
          // Get the most recent response status for each client
          initiativeActivities.forEach(activity => {
            const clientId = activity.client_id;
            const status = activity.response_status || 'no_response';
            const activityDate = new Date(activity.activity_date);
            
            if (!clientResponseMap.has(clientId) || 
                new Date(clientResponseMap.get(clientId + '_date') || 0) < activityDate) {
              clientResponseMap.set(clientId, status);
              clientResponseMap.set(clientId + '_date', activity.activity_date);
            }
          });

          // Count unique clients by their latest response status and create mapping
          const responseStatuses = {
            no_response: 0,
            not_interested: 0,
            interested: 0,
            proposal: 0
          };

          const clientsByStatus = {
            no_response: [] as { id: string; name: string }[],
            not_interested: [] as { id: string; name: string }[],
            interested: [] as { id: string; name: string }[],
            proposal: [] as { id: string; name: string }[]
          };

          clientResponseMap.forEach((status, key) => {
            if (!key.endsWith('_date') && status in responseStatuses) {
              responseStatuses[status as keyof typeof responseStatuses]++;
              const client = clients.find(c => c.id === key);
              if (client) {
                clientsByStatus[status as keyof typeof clientsByStatus].push(client);
              }
            }
          });

          // Count activity types
          const activityTypes = {
            email: 0,
            call: 0,
            meeting: 0,
            linkedin: 0,
            other: 0
          };

          initiativeActivities.forEach(activity => {
            const activityType = activity.type?.toLowerCase() || 'other';
            if (activityType in activityTypes) {
              activityTypes[activityType as keyof typeof activityTypes]++;
            } else {
              activityTypes.other++;
            }
          });
          
          const result = {
            ...initiative,
            files: files || [],
            stats: {
              clientCount,
              clients,
              responseStatuses,
              clientsByStatus,
              activityTypes
            }
          };
          
          console.log(`Completed processing for ${initiative.name}:`, {
            clientCount,
            responseStatuses,
            activityTypes
          });
          
          return result;
        })
      );
      
      console.log('Final initiatives with stats:', initiativesWithFilesAndStats.length);
      return initiativesWithFilesAndStats as Initiative[];
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    retry: 3, // Retry failed requests 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  // Create/Update initiative mutation
  const createOrUpdateInitiative = useMutation({
    mutationFn: async (data: { name: string; description: string; deployment_date: string; files: File[]; isEdit?: boolean; id?: string }) => {
      // No org_id needed anymore
      
      if (data.isEdit && data.id) {
        // Update existing initiative
        const { data: result, error } = await supabase
          .from('initiatives')
          .update({
            name: data.name,
            description: data.description,
            deployment_date: data.deployment_date || null
          })
          .eq('id', data.id)
          .select()
          .single();
        
        if (error) throw error;
        return result;
      } else {
        // Create new initiative
        const { data: result, error } = await supabase
          .from('initiatives')
          .insert({
            name: data.name,
            description: data.description,
            deployment_date: data.deployment_date || null,
            created_by: (await supabase.auth.getUser()).data.user?.id
          })
          .select()
          .single();
        
        if (error) throw error;

        // Upload files if any
        if (data.files.length > 0) {
          const uploadPromises = data.files.map(async (file) => {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Date.now()}_${Math.random().toString(36).slice(2)}.${fileExt}`;
            
            // Upload to storage
            const { error: uploadError } = await supabase.storage
              .from('initiative-files')
              .upload(fileName, file);
            
            if (uploadError) throw uploadError;

            // Save file record
            const { error: dbError } = await supabase
              .from('initiative_files')
              .insert({
                initiative_id: result.id,
                name: file.name,
                file_path: fileName,
                file_size: file.size,
                mime_type: file.type,
                uploaded_by: (await supabase.auth.getUser()).data.user?.id
              });
            
            if (dbError) throw dbError;
          });

          await Promise.all(uploadPromises);
        }

        return result;
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      setOpen(false);
      resetForm();
      toast({
        title: 'Success',
        description: variables.isEdit ? 'Initiative updated successfully' : 'Initiative created successfully'
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Upload files mutation
  const uploadFiles = useMutation({
    mutationFn: async ({ initiativeId, files }: { initiativeId: string; files: File[] }) => {
      setUploadingFiles(true);
      const uploadPromises = files.map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).slice(2)}.${fileExt}`;
        
        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('initiative-files')
          .upload(fileName, file);
        
        if (uploadError) throw uploadError;

        // Save file record
        const { error: dbError } = await supabase
          .from('initiative_files')
          .insert({
            initiative_id: initiativeId,
            name: file.name,
            file_path: fileName,
            file_size: file.size,
            mime_type: file.type,
            uploaded_by: (await supabase.auth.getUser()).data.user?.id
          });
        
        if (dbError) throw dbError;
      });

      await Promise.all(uploadPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      setUploadingFiles(false);
      toast({
        title: 'Success',
        description: 'Files uploaded successfully'
      });
    },
    onError: (error: any) => {
      setUploadingFiles(false);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Delete initiative mutation
  const deleteInitiative = useMutation({
    mutationFn: async (initiativeId: string) => {
      // First delete all files associated with this initiative
      const { data: files } = await supabase
        .from('initiative_files')
        .select('id, file_path')
        .eq('initiative_id', initiativeId);

      if (files && files.length > 0) {
        // Delete files from storage
        const filePaths = files.map(file => file.file_path);
        const { error: storageError } = await supabase.storage
          .from('initiative-files')
          .remove(filePaths);
        
        if (storageError) throw storageError;

        // Delete file records from database
        const { error: filesError } = await supabase
          .from('initiative_files')
          .delete()
          .eq('initiative_id', initiativeId);
        
        if (filesError) throw filesError;
      }

      // Delete the initiative
      const { error } = await supabase
        .from('initiatives')
        .delete()
        .eq('id', initiativeId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      toast({
        title: 'Success',
        description: 'Initiative deleted successfully'
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Failed to delete initiative: ${error.message}`,
        variant: 'destructive'
      });
    }
  });

  // Delete file mutation
  const deleteFile = useMutation({
    mutationFn: async ({ fileId, filePath }: { fileId: string; filePath: string }) => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('initiative-files')
        .remove([filePath]);
      
      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('initiative_files')
        .delete()
        .eq('id', fileId);
      
      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      toast({
        title: 'Success',
        description: 'File deleted successfully'
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isEdit = !!selectedInitiative;
    createOrUpdateInitiative.mutate({ 
      ...formData, 
      files: selectedFiles,
      isEdit,
      id: selectedInitiative?.id
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileUpload = (initiativeId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      uploadFiles.mutate({ initiativeId, files });
    }
  };

  const downloadFile = async (filePath: string, fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('initiative-files')
        .download(filePath);
      
      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to download file',
        variant: 'destructive'
      });
    }
  };

  // Filter and sort initiatives
  const filteredAndSortedInitiatives = React.useMemo(() => {
    let filtered = initiatives.filter(initiative => {
      const matchesSearch = 
        initiative.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (initiative.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      let matchesDate = true;
      if (selectedDate !== 'all') {
        const createdDate = new Date(initiative.created_at);
        const now = new Date();
        
        switch (selectedDate) {
          case 'today':
            matchesDate = createdDate.toDateString() === now.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = createdDate >= weekAgo;
            break;
          case 'month':
            matchesDate = createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear();
            break;
          case 'year':
            matchesDate = createdDate.getFullYear() === now.getFullYear();
            break;
        }
      }
      
      return matchesSearch && matchesDate;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (selectedSort) {
        case 'newest':
          // Use deployment_date for sorting, fallback to created_at if no deployment_date
          const aDate = a.deployment_date ? new Date(a.deployment_date) : new Date(a.created_at);
          const bDate = b.deployment_date ? new Date(b.deployment_date) : new Date(b.created_at);
          return bDate.getTime() - aDate.getTime();
        case 'oldest':
          const aDateOld = a.deployment_date ? new Date(a.deployment_date) : new Date(a.created_at);
          const bDateOld = b.deployment_date ? new Date(b.deployment_date) : new Date(b.created_at);
          return aDateOld.getTime() - bDateOld.getTime();
        case 'name':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });

    return filtered;
  }, [initiatives, searchTerm, selectedDate, selectedSort]);

  const handleEditInitiative = (initiative: Initiative) => {
    setFormData({
      name: initiative.name,
      description: initiative.description || '',
      deployment_date: initiative.deployment_date || ''
    });
    setSelectedInitiative(initiative);
    setOpen(true);
  };

  const handleDeleteInitiative = (initiative: Initiative) => {
    if (confirm(`Are you sure you want to delete "${initiative.name}"? This action cannot be undone and will also delete all associated files.`)) {
      deleteInitiative.mutate(initiative.id);
    }
  };

  const handleViewFiles = (initiative: Initiative) => {
    setSelectedInitiativeForFiles(initiative);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Initiatives</h1>
        
        <Dialog open={open} onOpenChange={handleModalClose}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Initiative
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedInitiative ? 'Edit Initiative' : 'Create New Initiative'}
              </DialogTitle>
              <DialogDescription>
                {selectedInitiative 
                  ? 'Update the initiative details below.'
                  : 'Add a new initiative to track organizational goals and projects.'
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="deployment_date">Deployment Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.deployment_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.deployment_date ? (
                        format(new Date(formData.deployment_date), "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.deployment_date ? new Date(formData.deployment_date) : undefined}
                      onSelect={(date) => 
                        setFormData({ 
                          ...formData, 
                          deployment_date: date ? date.toISOString().split('T')[0] : '' 
                        })
                      }
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* File Upload Section */}
              <div>
                <Label htmlFor="files">Files (Optional)</Label>
                <div className="space-y-2">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload-new"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('file-upload-new')?.click()}
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Files
                  </Button>
                  
                  {/* Selected Files Preview */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm font-medium">Selected Files:</p>
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                          <span className="text-sm truncate">{file.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <Button type="submit" disabled={createOrUpdateInitiative.isPending} className="w-full">
                {createOrUpdateInitiative.isPending 
                  ? (selectedInitiative ? 'Updating...' : 'Creating...') 
                  : (selectedInitiative ? 'Update Initiative' : 'Create Initiative')
                }
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <InitiativeFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedSort={selectedSort}
        setSelectedSort={setSelectedSort}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />

      {/* Initiatives List */}
      <InitiativeList
        initiatives={filteredAndSortedInitiatives}
        isLoading={isLoading}
        onEdit={handleEditInitiative}
        onDelete={handleDeleteInitiative}
        onViewFiles={handleViewFiles}
      />

      {/* Files Dialog */}
      <Dialog open={!!selectedInitiativeForFiles} onOpenChange={() => setSelectedInitiativeForFiles(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Files for {selectedInitiativeForFiles?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {selectedInitiativeForFiles?.files?.map((file) => (
              <div key={file.id} className="flex items-center justify-between p-2 border rounded">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span>{file.name}</span>
                  {file.file_size && (
                    <span className="text-xs text-muted-foreground">
                      ({(file.file_size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadFile(file.file_path, file.name)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteFile.mutate({ fileId: file.id, filePath: file.file_path })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {(!selectedInitiativeForFiles?.files || selectedInitiativeForFiles.files.length === 0) && (
              <p className="text-muted-foreground text-center py-4">No files uploaded yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}